import * as Sentry from "@sentry/react"
import { createContext, type ReactNode, useContext, useEffect } from "react"
import { Skeleton } from "../components/ui/skeleton"
import { type TUser, useCurrentUserQuery } from "../hooks/useCurrentUserQuery"
import { useActiveWorkoutStore, useStatsStore } from "../store"
import { calculateTotalWeight } from "../utils/workoutUtils"

const CurrentUserContext = createContext<TUser | null>(null)

export const useCurrentUserContext = () => {
  const currentUser = useContext(CurrentUserContext)
  if (!currentUser) {
    /* During HMR in dev mode, React can temporarily unmount and remount components
     * in isolation, sometimes before the full tree (including context providers)
     * is restored. That means useContext(CurrentUserContext) may temporarily return
     * null, even if the provider will exist again shortly.
     * */
    return {
      user: {
        email: "",
        name: "",
        userId: "",
        dateOfLastLogin: "",
        dateCreated: "",
      },
      numberOfCompletedWorkouts: 0,
      totalWeightLifted: 0,
      totalWeightLiftedThisYear: 0,
      weekStreak: 0,
    } satisfies TUser
  }

  Sentry.setUser({
    email: currentUser?.user?.email,
    username: currentUser?.user?.name || "",
  })

  return currentUser
}

export const CurrentUserContextProvider = ({ children }: { children: ReactNode }) => {
  const currentUserQuery = useCurrentUserQuery()
  const { setStats } = useStatsStore((state) => state)
  const { activeWorkout } = useActiveWorkoutStore((state) => state)

  useEffect(() => {
    const activeWorkoutWeightLifted = activeWorkout ? calculateTotalWeight(activeWorkout) : 0
    setStats({
      totalWeightLifted: (currentUserQuery.data?.totalWeightLifted || 0) + activeWorkoutWeightLifted,
      totalWeightLiftedThisYear: (currentUserQuery.data?.totalWeightLiftedThisYear || 0) + activeWorkoutWeightLifted,
    })
  }, [currentUserQuery.data?.totalWeightLifted, activeWorkout])

  if (currentUserQuery.isPending) {
    return <Skeleton className="h-[100vh] w-full" />
  }

  if (currentUserQuery.isError) {
    return <p>Error loading user</p>
  }

  return <CurrentUserContext.Provider value={currentUserQuery.data}>{children}</CurrentUserContext.Provider>
}
